import { useState, useEffect } from 'react'
import { X, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import apiService from '../services/api'
import { getImageUrl } from '../utils/apiUrl'
import { sanitizeHtml } from '../utils/sanitizeHtml'

const PopupEvent = () => {
  const [popupEvent, setPopupEvent] = useState(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchActivePopupEvent()
  }, [])

  const shouldDisplayPopup = (event) => {
    if (!event) return false

    const dismissedKey = `popup-event-dismissed-${event.id}`
    const frequency = event.display_frequency || 'once_per_session'

    // Vérifier selon la fréquence d'affichage
    switch (frequency) {
      case 'once_per_session':
        // Une fois par session (dans localStorage pour la session)
        return !sessionStorage.getItem(dismissedKey)
      
      case 'once_per_day':
        // Une fois par jour
        const lastDismissed = localStorage.getItem(dismissedKey)
        if (!lastDismissed) return true
        const lastDate = new Date(lastDismissed)
        const today = new Date()
        return lastDate.toDateString() !== today.toDateString()
      
      case 'once_per_week':
        // Une fois par semaine
        const lastDismissedWeek = localStorage.getItem(dismissedKey)
        if (!lastDismissedWeek) return true
        const lastWeekDate = new Date(lastDismissedWeek)
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        return lastWeekDate < weekAgo
      
      case 'every_time':
        // À chaque visite (ne pas vérifier localStorage)
        return true
      
      default:
        return !localStorage.getItem(dismissedKey)
    }
  }

  const fetchActivePopupEvent = async () => {
    try {
      setIsLoading(true)
      const response = await apiService.get('/popup-events/active')
      
      if (response.data && shouldDisplayPopup(response.data)) {
        setPopupEvent(response.data)
        
        // Appliquer le délai avant affichage
        const delay = (response.data.delay_seconds || 0) * 1000
        setTimeout(() => {
          setIsVisible(true)
        }, delay)
      }
    } catch (error) {
      console.error('Erreur chargement popup event:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (popupEvent) {
      const dismissedKey = `popup-event-dismissed-${popupEvent.id}`
      const frequency = popupEvent.display_frequency || 'once_per_session'
      
      // Enregistrer selon la fréquence
      switch (frequency) {
        case 'once_per_session':
          sessionStorage.setItem(dismissedKey, 'true')
          break
        case 'once_per_day':
        case 'once_per_week':
          localStorage.setItem(dismissedKey, new Date().toISOString())
          break
        case 'every_time':
          // Ne rien enregistrer pour "à chaque visite"
          break
        default:
          localStorage.setItem(dismissedKey, 'true')
      }
    }
    setIsVisible(false)
  }

  if (isLoading || !isVisible || !popupEvent) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Bouton fermer */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer la popup"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Image (si présente) */}
        {popupEvent.image_path && (
          <div className="w-full h-64 md:h-80 overflow-hidden rounded-t-lg">
            <img
              src={getImageUrl(popupEvent.image_path)}
              alt={popupEvent.title || 'Événement'}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none'
              }}
            />
          </div>
        )}

        {/* Contenu */}
        <div className="p-6 md:p-8">
          {/* Titre */}
          {popupEvent.title && (
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {popupEvent.title}
            </h2>
          )}

          {/* Message */}
          {popupEvent.message && (
            <div 
              className="text-gray-700 mb-6 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(popupEvent.message.replace(/\n/g, '<br />')) }}
            />
          )}

          {/* Lien (si présent) */}
          {popupEvent.link && (
            <div className="mt-6">
              {popupEvent.link.startsWith('http') ? (
                <a
                  href={popupEvent.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                >
                  {popupEvent.link_text || 'En savoir plus'}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              ) : (
                <Link
                  to={popupEvent.link}
                  onClick={handleClose}
                  className="inline-flex items-center px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                >
                  {popupEvent.link_text || 'En savoir plus'}
                </Link>
              )}
            </div>
          )}

          {/* Bouton fermer en bas */}
          <div className="mt-6 text-center">
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-sm underline"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PopupEvent
